import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { X, MessageSquare, Send, Smartphone, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";
import widgetIcon from "@/assets/widget-icon.webp";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface WidgetPosition {
  position_horizontal: string;
  position_vertical: string;
  offset_horizontal: number;
  offset_vertical: number;
  is_enabled: boolean;
}

const ChatWidget = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<WidgetPosition>({
    position_horizontal: 'right',
    position_vertical: 'bottom',
    offset_horizontal: 24,
    offset_vertical: 24,
    is_enabled: true
  });
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    message: "",
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Fetch widget position settings
  useEffect(() => {
    const fetchWidgetSettings = async () => {
      try {
        const { data, error } = await supabase
          .from('widget_settings' as any)
          .select('*')
          .limit(1)
          .maybeSingle();

        if (data && !error && typeof data === 'object') {
          setPosition(data as WidgetPosition);
        }
      } catch (error) {
        console.error('Error fetching widget settings:', error);
      }
    };

    fetchWidgetSettings();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Basic phone validation before invoking backend
      const phoneRegex = /^\+?[1-9]\d{1,14}$/;
      const cleaned = formData.phone.replace(/[\s()-]/g, '');
      if (!phoneRegex.test(cleaned)) {
        toast({
          title: "Invalid phone number",
          description: "Please enter a valid phone number",
          variant: "destructive"
        });
        setIsLoading(false);
        return;
      }

      // Use backend function to create conversation, store message and forward via Twilio
      const { data, error } = await supabase.functions.invoke('widget-submit', {
        body: {
          name: formData.name,
          phone: formData.phone,
          message: formData.message,
        },
      });

      if (error) throw error;

      setIsSubmitted(true);
      toast({
        title: "Message sent!",
        description: "An agent will respond to you shortly"
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (!position.is_enabled) {
    return null;
  }

  const getPositionStyles = () => {
    const styles: React.CSSProperties = {
      position: 'fixed',
      zIndex: 50
    };

    // Horizontal positioning
    if (position.position_horizontal === 'left') {
      styles.left = `${position.offset_horizontal}px`;
    } else if (position.position_horizontal === 'right') {
      styles.right = `${position.offset_horizontal}px`;
    } else {
      styles.left = '50%';
      styles.transform = 'translateX(-50%)';
    }

    // Vertical positioning
    if (position.position_vertical === 'top') {
      styles.top = `${position.offset_vertical}px`;
    } else {
      styles.bottom = `${position.offset_vertical}px`;
    }

    return styles;
  };

  return (
    <div style={getPositionStyles()} className="flex flex-col items-center gap-2">
      {/* Widget Button */}
      {!isOpen && (
        <>
          <div className="text-sm font-semibold text-foreground bg-background px-3 py-1 rounded-full shadow-md">
            Text us
          </div>
          <button
            onClick={() => setIsOpen(true)}
            className="h-20 w-20 rounded-full bg-gradient-to-br from-primary to-accent shadow-[var(--shadow-hover)] hover:scale-110 transition-transform duration-300 flex flex-col items-center justify-center group focus:outline-none focus:ring-4 focus:ring-ring gap-1"
          >
            <Smartphone className="h-8 w-8 text-white" aria-hidden="true" />
            <span className="text-xs text-white font-semibold">Text</span>
            <span className="sr-only">Open live chat</span>
          </button>
        </>
      )}

      {/* Widget Window */}
      {isOpen && (
        <Card className="w-[92vw] max-w-[380px] h-auto min-h-[600px] max-h-[90vh] shadow-[var(--shadow-hover)] border-4 border-primary flex flex-col overflow-hidden" role="dialog" aria-labelledby="chat-title" aria-modal="true">
          {/* Header */}
          <div className="py-2 px-4 bg-gradient-to-r from-primary via-primary-glow to-accent text-primary-foreground">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src={widgetIcon} alt="iQ Texting chat widget icon" className="h-8 w-8" />
                <div>
                  <h3 id="chat-title" className="font-semibold">Chat with us</h3>
                  <div className="flex items-center gap-1.5 text-xs opacity-90">
                    <div className="h-2 w-2 rounded-full bg-accent-foreground animate-pulse-soft" aria-hidden="true" />
                    <span className="text-sm" aria-live="polite">We&apos;re online</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                aria-label="Close chat widget"
                className="hover:bg-primary-foreground/20 rounded-full p-1.5 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-foreground"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 p-5 pb-safe bg-muted/20">
            {!isSubmitted ? (
              <form onSubmit={handleSubmit} className="space-y-2">
                <div>
                  <p className="text-base text-muted-foreground mb-3 leading-none">
                    Fill out the form below and we'll connect you with an agent right away.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <div>
                <label htmlFor="name" className="text-base font-medium mb-1.5 block leading-none">
                  Your Name
                </label>
                    <Input
                      id="name"
                      name="name"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  
                  <div>
                <label htmlFor="phone" className="text-base font-medium mb-1.5 block leading-none">
                  Phone Number
                </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  
                  <div>
                <label htmlFor="message" className="text-base font-medium mb-1.5 block leading-none">
                  Message
                </label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="How can we help you?"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={3}
                    />
                  </div>
                </div>

                <Button type="submit" className="w-full gap-2 bg-gradient-to-r from-primary to-accent" disabled={isLoading} aria-live="polite">
                  <Send className="h-4 w-4" aria-hidden="true" />
                  {isLoading ? "Sending..." : "Start Chat"}
                </Button>

                <div className="relative my-2">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-muted/20 px-2 text-muted-foreground">Or</span>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full gap-2 mb-0"
                  onClick={() => navigate('/book')}
                >
                  <Calendar className="h-4 w-4" />
                  Book an Appointment
                </Button>
              </form>
            ) : (
              <div className="text-center py-8">
                <div className="h-16 w-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="h-8 w-8 text-accent" />
                </div>
                <h4 className="font-semibold mb-2">Connecting you now!</h4>
                <p className="text-base text-muted-foreground">
                  An agent will respond to you shortly...
                </p>
                <div className="flex items-center justify-center gap-1 mt-4">
                  <div className="h-2 w-2 rounded-full bg-accent animate-pulse-soft" />
                  <div className="h-2 w-2 rounded-full bg-accent animate-pulse-soft animation-delay-200" />
                  <div className="h-2 w-2 rounded-full bg-accent animate-pulse-soft animation-delay-400" />
                </div>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};

export default ChatWidget;
